import clsx from "clsx";
import { FC, ReactNode } from "react";

import { formatDateTime, formatTime } from "@tsmono/util";

import { AsciinemaPlayer } from "./AsciinemaPlayer";
import styles from "./HumanBaselineView.module.css";
import { LightboxCarousel } from "./LightboxCarousel";

export interface SessionLog {
  name?: string;
  user?: string;
  input: string;
  output: string;
  timing: string;
}

interface HumanBaselineViewProps {
  id: string;
  started?: Date;
  running: boolean;
  completed?: boolean;
  runtime?: number;
  answer?: string;
  sessionLogs: SessionLog[];
}

/**
 * Renders the HumanBaselineView component.
 */
export const HumanBaselineView: FC<HumanBaselineViewProps> = ({
  id,
  started,
  runtime,
  answer,
  completed,
  running,
  sessionLogs,
}) => {
  const player_fns: Array<{ label: string; render: () => ReactNode }> = [];

  // Make a player for each session log
  let count = 1;
  let maxCols = 0;

  for (const sessionLog of sessionLogs) {
    const rows = extractSize(sessionLog.output, "LINES", 24);
    const cols = extractSize(sessionLog.output, "COLUMNS", 80);
    maxCols = Math.max(maxCols, cols);

    const currentCount = count;
    const title =
      sessionLogs.length === 1
        ? "Terminal Session"
        : `Terminal Session ${currentCount}`;

    player_fns.push({
      label: title,
      render: () => (
        <AsciinemaPlayer
          id={`${id}-player-${currentCount}`}
          input={sessionLog.input}
          output={sessionLog.output}
          timing={sessionLog.timing}
          rows={rows}
          cols={cols}
          style={{
            height: `${rows * 2}em`,
            width: `${cols * 2}em`,
          }}
          fit="both"
        />
      ),
    });
    count += 1;
  }

  return (
    <div className={styles.asciinemaWrapper}>
      <div className={styles.asciinemaContainer}>
        <div className={clsx(styles.asciinemaHeaderLeft, "text-style-label")}>
          {started ? formatDateTime(started) : ""}
          {runtime ? ` (${formatTime(Math.floor(runtime))})` : ""}
        </div>
        <div
          className={clsx(styles.asciinemaHeaderCenter, "text-style-label")}
        ></div>
        <div className={styles.asciinemaHeaderRight}>
          <StatusMessage
            completed={completed}
            running={running}
            answer={answer}
          />
        </div>
        <div className={styles.asciinemaBody}>
          <LightboxCarousel id={`${id}-ascii-cinema`} slides={player_fns} />
        </div>
      </div>
    </div>
  );
};

interface StatusMessageProps {
  completed?: boolean;
  running?: boolean;
  answer?: string;
}

const StatusMessage: FC<StatusMessageProps> = ({
  completed,
  running,
  answer,
}) => {
  if (running) {
    return <span className={"text-style-label"}>Running</span>;
  } else if (completed) {
    return (
      <div>
        <span
          className={clsx(
            "text-style-label",
            "text-style-secondary",
            styles.asciinemaPlayerStatus
          )}
        >
          Answer
        </span>
        <span>{answer}</span>
      </div>
    );
  } else {
    return "Unknown status";
  }
};

/**
 * Extracts a numeric size value from a string based on a given label.
 *
 * Searches the input string for a pattern matching the format `LABEL="VALUE"`,
 * where `LABEL` is the provided label and `VALUE` is a numeric value.
 */
const extractSize = (
  value: string,
  label: string,
  defaultValue: number
): number => {
  const regex = new RegExp(`${label}="(\\d+)"`);
  const match = value.match(regex);
  const size = match ? match[1] : undefined;
  if (size) {
    return parseInt(size);
  } else {
    return defaultValue;
  }
};
